import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AccessibleModal from './AccessibleModal';
import {
  popoverContainerStyle,
  popoverHeaderStyle,
  popoverMessageStyle,
  popoverSubmitButtonStyle
} from './PopoverStyles';

/**
 * Issue #321: Announcement Dialog Component
 * Displays active announcements from admin to all users
 * Uses AccessibleModal for keyboard accessibility (WCAG 2.1 compliant)
 *
 * - Fetches announcements on mount (public endpoint, no auth required)
 * - Shows announcements one at a time
 * - Users can dismiss announcements
 * - Issue #327: Dismissed announcements are stored in localStorage to persist
 *   across app restarts within the same login session (cleared on logout)
 */
const AnnouncementDialog = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
    } catch {
      return [];
    }
  });

  // Fetch active announcements on mount only
  // Issue #326: Removed 'dismissed' from dependencies to prevent race condition
  // where re-filtering + index increment caused subsequent announcements to flash
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        // Issue #327: Read dismissed IDs from localStorage (persists across app restarts)
        const dismissedIds = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
        const response = await axios.get('/api/announcements');
        // Filter out already dismissed announcements
        const activeAnnouncements = response.data.filter(
          (a) => !dismissedIds.includes(a.id)
        );
        setAnnouncements(activeAnnouncements);
      } catch (error) {
        // Silently fail - announcements are not critical
        console.error('[Announcements] Failed to fetch:', error);
      }
    };

    fetchAnnouncements();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle dismissing the current announcement
  // Issue #327: Use localStorage to persist across app restarts
  const handleDismiss = useCallback(() => {
    const currentAnnouncement = announcements[currentIndex];
    if (currentAnnouncement) {
      const newDismissed = [...dismissed, currentAnnouncement.id];
      setDismissed(newDismissed);
      localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
    }
    // Move to next announcement or close
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setAnnouncements([]);
    }
  }, [announcements, currentIndex, dismissed]);

  // Get current announcement
  const currentAnnouncement = announcements[currentIndex];

  // Don't render if no announcements
  if (!currentAnnouncement) return null;

  // Determine icon based on announcement type
  const getIcon = (type) => {
    switch (type) {
      case 'warning':
        return { icon: '!', className: 'announcement-icon--warning' };
      case 'maintenance':
        return { icon: '!', className: 'announcement-icon--maintenance' };
      default:
        return { icon: 'i', className: 'announcement-icon--info' };
    }
  };

  const { icon, className } = getIcon(currentAnnouncement.type);

  // Container style with explicit white background for readability
  const containerStyle = {
    ...popoverContainerStyle,
    background: '#FFFFFF'
  };

  return (
    <AccessibleModal
      isOpen={true}
      onClose={handleDismiss}
      titleId="announcement-dialog-title"
      containerStyle={containerStyle}
    >
      {/* Icon */}
      <div
        className={`announcement-icon ${className}`}
        aria-hidden="true"
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#FFFFFF',
          marginBottom: '8px',
          alignSelf: 'center',
          background: currentAnnouncement.type === 'warning' ? '#f59e0b' :
                      currentAnnouncement.type === 'maintenance' ? '#ef4444' : 'var(--brand-primary)'
        }}
      >
        {icon}
      </div>

      {/* Title */}
      <h2
        id="announcement-dialog-title"
        style={{ ...popoverHeaderStyle, textAlign: 'center', color: '#1a1a2e' }}
      >
        {currentAnnouncement.title}
      </h2>

      {/* Message */}
      <p
        style={{ ...popoverMessageStyle, textAlign: 'center', whiteSpace: 'pre-line', color: '#333333' }}
      >
        {currentAnnouncement.message}
      </p>

      {/* Close button */}
      <button
        onClick={handleDismiss}
        style={{ ...popoverSubmitButtonStyle, width: '100%', marginTop: '8px' }}
        type="button"
        aria-label="Ankündigung schließen"
      >
        OK
      </button>
    </AccessibleModal>
  );
};

export default AnnouncementDialog;
